import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faTrash,
  faChevronDown,
  faChevronRight,
  faComments,
  faSave,
} from '@fortawesome/free-solid-svg-icons';
import { useDeal, useUpdateDeal, useDeleteDeal, useDealClients } from '@/api/useDeals';
import { DealChatSidebar } from '@/components/Deals/DealChatSidebar';
import { Button } from '@/components/ui';
import { Input, Select } from '@/components/forms';
import { ROUTES } from '@/constants/routes';
import { DEAL_STATUS_ORDER, DEAL_STATUS_LABELS, DEAL_STATUS_COLORS } from '@/types/deal';
import type { DealStatus } from '@/types/deal';
import styles from './DealDetail.module.css';

export const DealDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dealId = id ?? null;
  const { data: deal, isLoading } = useDeal(dealId);
  const updateDeal = useUpdateDeal();
  const deleteDeal = useDeleteDeal();

  const [clientName, setClientName] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<DealStatus>('lead');
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [chatOpen, setChatOpen] = useState(true);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { data: clientSuggestions = [] } = useDealClients(clientName);

  // Detail fields
  const [clientContact, setClientContact] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [guestCount, setGuestCount] = useState('');
  const [estimatedBudget, setEstimatedBudget] = useState('');
  const [currency, setCurrency] = useState('PEN');

  useEffect(() => {
    if (deal) {
      setClientName(deal.client_name);
      setNotes(deal.notes);
      setStatus(deal.status);
      setClientContact(deal.client_contact);
      setClientEmail(deal.client_email);
      setClientPhone(deal.client_phone);
      setEventType(deal.event_type);
      setEventDate(deal.event_date || '');
      setEventLocation(deal.event_location);
      setGuestCount(deal.guest_count?.toString() || '');
      setEstimatedBudget(deal.estimated_budget?.toString() || '');
      setCurrency(deal.currency);
      setDirty(false);
    }
  }, [deal]);

  const markDirty = () => {
    if (!dirty) setDirty(true);
  };

  const handleSave = () => {
    if (!dealId) return;
    updateDeal.mutate(
      {
        id: dealId,
        data: {
          title: clientName.trim(),
          client_name: clientName.trim(),
          notes,
          status,
          client_contact: clientContact,
          client_email: clientEmail,
          client_phone: clientPhone,
          event_type: eventType,
          event_date: eventDate || null,
          event_location: eventLocation,
          guest_count: guestCount ? parseInt(guestCount) || null : null,
          estimated_budget: estimatedBudget ? parseFloat(estimatedBudget) || null : null,
          currency,
        },
      },
      {
        onSuccess: () => setDirty(false),
      },
    );
  };

  const handleBack = () => {
    queryClient.invalidateQueries({ queryKey: ['deals'] });
    navigate(ROUTES.dashboardDeals);
  };

  const handleStatusChange = (newStatus: DealStatus) => {
    setStatus(newStatus);
    markDirty();
  };

  const pickClient = (name: string) => {
    setClientName(name);
    setShowClientSuggestions(false);
    markDirty();
  };

  const filteredClients = clientSuggestions.filter(
    (s) => s.toLowerCase() !== clientName.toLowerCase(),
  );

  const handleDelete = () => {
    if (!dealId || !confirm('Delete this deal?')) return;
    deleteDeal.mutate(dealId, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['deals'] });
        navigate(ROUTES.dashboardDeals);
      },
    });
  };

  if (isLoading || !deal) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.infoPanel}>
        <div className={styles.topBar}>
          <button className={styles.backButton} onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> Deals
          </button>
          <div className={styles.topActions}>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={!dirty || updateDeal.isPending}
            >
              <FontAwesomeIcon icon={faSave} /> {updateDeal.isPending ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="danger" size="sm" onClick={handleDelete}>
              <FontAwesomeIcon icon={faTrash} /> Delete
            </Button>
          </div>
        </div>

        <div className={styles.clientNameWrapper}>
          <input
            className={styles.clientNameInput}
            value={clientName}
            onChange={(e) => {
              setClientName(e.target.value);
              setShowClientSuggestions(true);
              markDirty();
            }}
            onFocus={() => setShowClientSuggestions(true)}
            onBlur={() => setTimeout(() => setShowClientSuggestions(false), 150)}
            placeholder="Company name"
          />
          {showClientSuggestions && filteredClients.length > 0 && clientName.length >= 1 && (
            <div className={styles.clientSuggestions}>
              {filteredClients.map((name) => (
                <button
                  key={name}
                  className={styles.clientSuggestion}
                  onMouseDown={() => pickClient(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.statusRow}>
          {DEAL_STATUS_ORDER.map((s) => (
            <button
              key={s}
              className={`${styles.statusChip} ${status === s ? styles.statusChipActive : ''}`}
              style={
                status === s
                  ? { background: DEAL_STATUS_COLORS[s], borderColor: DEAL_STATUS_COLORS[s] }
                  : undefined
              }
              onClick={() => handleStatusChange(s)}
            >
              {DEAL_STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        {/* Notes section */}
        <Input
          name="notes"
          label="Information"
          value={notes}
          setValue={(v) => {
            setNotes(v);
            markDirty();
          }}
          multiline
          rows={8}
          placeholder="Deal details, event info, requirements, meeting notes..."
        />

        {/* Details section */}
        <button className={styles.detailsToggle} onClick={() => setDetailsOpen(!detailsOpen)}>
          <FontAwesomeIcon
            icon={detailsOpen ? faChevronDown : faChevronRight}
            className={styles.detailsIcon}
          />
          Event & Company Details
        </button>

        {detailsOpen && (
          <div className={styles.detailsGrid}>
            <Input
              name="event_type"
              label="Event Type"
              value={eventType}
              setValue={(v) => {
                setEventType(v);
                markDirty();
              }}
              placeholder="e.g. Christmas Party"
            />
            <Input
              name="event_date"
              label="Event Date"
              type="date"
              value={eventDate}
              setValue={(v) => {
                setEventDate(v);
                markDirty();
              }}
              placeholder="mm/dd/yyyy"
            />
            <Input
              name="event_location"
              label="Location"
              value={eventLocation}
              setValue={(v) => {
                setEventLocation(v);
                markDirty();
              }}
              placeholder="City or venue"
            />
            <Input
              name="guest_count"
              label="Guests"
              type="number"
              value={guestCount}
              setValue={(v) => {
                setGuestCount(v);
                markDirty();
              }}
              placeholder="e.g. 200"
            />
            <div className={styles.budgetGroup}>
              <Select
                label="Currency"
                value={currency}
                onChange={(e) => {
                  setCurrency(e.target.value);
                  markDirty();
                }}
                options={[
                  { value: 'PEN', label: 'PEN' },
                  { value: 'USD', label: 'USD' },
                  { value: 'EUR', label: 'EUR' },
                  { value: 'MXN', label: 'MXN' },
                  { value: 'COP', label: 'COP' },
                ]}
              />
              <Input
                name="estimated_budget"
                label="Budget"
                type="number"
                value={estimatedBudget}
                setValue={(v) => {
                  setEstimatedBudget(v);
                  markDirty();
                }}
                placeholder="e.g. 50000"
              />
            </div>
            <Input
              name="client_contact"
              label="Contact"
              value={clientContact}
              setValue={(v) => {
                setClientContact(v);
                markDirty();
              }}
              placeholder="Contact name"
            />
            <Input
              name="client_email"
              label="Email"
              type="email"
              value={clientEmail}
              setValue={(v) => {
                setClientEmail(v);
                markDirty();
              }}
              placeholder="email@company.com"
            />
            <Input
              name="client_phone"
              label="Phone"
              type="tel"
              value={clientPhone}
              setValue={(v) => {
                setClientPhone(v);
                markDirty();
              }}
              placeholder="+1 555 555 5555"
            />
          </div>
        )}
      </div>

      {/* Chat sidebar — desktop */}
      {chatOpen && (
        <div className={`${styles.chatPanel} ${chatExpanded ? styles.chatPanelExpanded : ''}`}>
          <DealChatSidebar
            sessionId={deal.chat_session_id}
            dealTitle={deal.client_name}
            onClose={() => setChatOpen(false)}
            onToggleExpand={() => setChatExpanded(!chatExpanded)}
            isExpanded={chatExpanded}
          />
        </div>
      )}
      {!chatOpen && (
        <button
          className={styles.chatOpenButton}
          onClick={() => setChatOpen(true)}
          title="Open chat"
        >
          <FontAwesomeIcon icon={faComments} />
        </button>
      )}

      {/* Chat sidebar — mobile slide-over */}
      {mobileChatOpen && (
        <div className={styles.mobileChatOverlay} onClick={() => setMobileChatOpen(false)} />
      )}
      <div className={`${styles.mobileChatPanel} ${mobileChatOpen ? styles.mobileChatPanelOpen : ''}`}>
        <DealChatSidebar
          sessionId={deal.chat_session_id}
          dealTitle={deal.client_name}
          onClose={() => setMobileChatOpen(false)}
        />
      </div>
      <button
        className={styles.mobileChatFab}
        onClick={() => setMobileChatOpen(true)}
        aria-label="Open chat"
      >
        <FontAwesomeIcon icon={faComments} />
      </button>
    </div>
  );
};
